import React, { useState, useEffect } from 'react';
import FormModal from '@shared/ui/FormModal';

// Import the ApiConfiguration type
interface ApiConfiguration {
  authentication: {
    type: 'none' | 'api_key' | 'bearer_token' | 'oauth' | 'basic_auth' | 'custom';
    credentials: {
      api_key?: string;
      api_secret?: string;
      bearer_token?: string;
      username?: string;
      password?: string;
      oauth_client_id?: string;
      oauth_client_secret?: string;
    };
    headers?: Record<string, string>;
  };
  endpoint: {
    base_url: string;
    version?: string;
    endpoints?: Record<string, string>;
  };
  rate_limits: {
    requests_per_hour?: number;
    requests_per_minute?: number;
    burst_limit?: number;
  };
  data_mapping?: Record<string, string>;
  filters?: Record<string, string>;
}

interface EditDataSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (id: number, data: any) => void;
  dataSource: any;
}

const EditDataSourceModal: React.FC<EditDataSourceModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  dataSource 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'CSV Upload',
    schedule: 'Daily',
    status: 'Active',
    config: {}
  });

  const [apiConfig, setApiConfig] = useState<ApiConfiguration | undefined>(undefined);
  /** Empty string = use scheduler default (e.g. 03:00 UTC); 0–23 = hour in UTC */
  const [scheduledUtcHourInput, setScheduledUtcHourInput] = useState('');

  const scheduleUsesUtcHour = (schedule: string) =>
    ['Daily', 'Weekly', 'Monthly'].includes(schedule);

  // Initialize form data when dataSource changes
  useEffect(() => {
    if (dataSource) {
      setFormData({
        name: dataSource.name || '',
        description: dataSource.description || '',
        type: dataSource.type || 'CSV Upload',
        schedule: dataSource.schedule || 'Daily',
        status: dataSource.status || 'Active',
        config: dataSource.config || {}
      });

      const c = dataSource.config as Record<string, unknown> | undefined;
      const existing =
        c?.scheduledUtcHour !== undefined && c?.scheduledUtcHour !== null
          ? c.scheduledUtcHour
          : c?.dailyUtcHour;
      if (existing !== undefined && existing !== null && String(existing).trim() !== '') {
        setScheduledUtcHourInput(String(existing));
      } else {
        setScheduledUtcHourInput('');
      }

      // Extract API config if it exists
      if (dataSource.config?.api) {
        setApiConfig(dataSource.config.api);
      } else {
        setApiConfig(undefined);
      }
    }
  }, [dataSource]);

  const buildConfigForSubmit = (): Record<string, unknown> => {
    const base: Record<string, unknown> = { ...(formData.config || {}) };

    if (!scheduleUsesUtcHour(formData.schedule)) {
      delete base.scheduledUtcHour;
      delete base.dailyUtcHour;
    } else {
      const trimmed = scheduledUtcHourInput.trim();
      if (trimmed === '') {
        delete base.scheduledUtcHour;
        delete base.dailyUtcHour;
      } else {
        const n = parseInt(trimmed, 10);
        if (Number.isFinite(n) && n >= 0 && n <= 23) {
          base.scheduledUtcHour = n;
          delete base.dailyUtcHour;
        }
      }
    }

    if (isApiType(formData.type) && apiConfig) {
      return { ...base, api: apiConfig };
    }
    return base;
  };

  const handleSubmit = () => {
    if (!dataSource) return;

    const submissionData = {
      ...formData,
      config: buildConfigForSubmit()
    };

    onSubmit(dataSource.id, submissionData);
    onClose();
  };

  const isApiType = (type: string) => {
    return ['Real-time API', 'Batch API', 'REST API', 'GraphQL API', 'Government API', 'Third-party API'].includes(type);
  };

  const handleTypeChange = (type: string) => {
    setFormData({ ...formData, type });
    if (!isApiType(type)) {
      setApiConfig(undefined);
    }
  };

  // Nothing to edit without a source — guard here rather than on `isOpen` so the
  // dialog itself still owns open/closed.
  if (!dataSource) return null;

  return (
    <FormModal
      isOpen={isOpen}
      title="Edit data source"
      onClose={onClose}
      onSubmit={handleSubmit}
      size={isApiType(formData.type) ? 'xl' : 'md'}
      submitLabel="Update source"
    >
      <div className="space-y-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
            Name *
          </span>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="input"
            placeholder="Enter data source name"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
            Description
          </span>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="input"
            rows={3}
            placeholder="Enter description"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
            Type *
          </span>
          <select
            required
            value={formData.type}
            onChange={(e) => handleTypeChange(e.target.value)}
            className="input"
          >
            <optgroup label="File-based sources">
              <option value="CSV Upload">CSV Upload</option>
              <option value="Web Scraper">Web Scraper</option>
            </optgroup>
            <optgroup label="API sources">
              <option value="Government API">Government API</option>
            </optgroup>
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
            Schedule *
          </span>
          <select
            required
            value={formData.schedule}
            onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
            className="input"
          >
            <option value="Manual">Manual</option>
            <option value="Hourly">Hourly</option>
            <option value="Daily">Daily</option>
            <option value="Weekly">Weekly</option>
            <option value="Monthly">Monthly</option>
          </select>
        </label>

        {scheduleUsesUtcHour(formData.schedule) && (
          <div>
            <label
              htmlFor="scheduledUtcHour"
              className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary"
            >
              Run hour (UTC)
            </label>
            <input
              id="scheduledUtcHour"
              type="number"
              min={0}
              max={23}
              step={1}
              value={scheduledUtcHourInput}
              onChange={(e) => setScheduledUtcHourInput(e.target.value)}
              className="input"
              placeholder="Default (03:00 UTC)"
            />
            <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Optional. Used by the hourly scheduler Lambda for Daily / Weekly / Monthly. Leave empty
              for the default hour (03:00 UTC). Enter 10 for a 10:00 UTC run.
            </p>
          </div>
        )}

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
            Status *
          </span>
          <select
            required
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="input"
          >
            <option value="Active">Enable</option>
            <option value="Paused">Disable</option>
          </select>
        </label>
      </div>
    </FormModal>
  );
};

export default EditDataSourceModal;
